import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { DollarSign, CheckCircle, Camera, FileText, CreditCard, ArrowRight, Loader2, AlertCircle, Wine, GlassWater } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function IncentiveCard({ icon: Icon, amount, title, description }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm card-hover">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-3xl font-bold text-green-600">${amount}</span>
            <span className="text-slate-500 text-sm">per placement</span>
          </div>
          <h3 className="font-semibold text-navy mb-1">{title}</h3>
          <p className="text-sm text-slate-600">{description}</p>
        </div>
      </div>
    </div>
  );
}

function Step({ number, title, description }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 rounded-full bg-copper text-white flex items-center justify-center font-bold flex-shrink-0">
        {number}
      </div>
      <div>
        <h3 className="font-semibold text-navy mb-1">{title}</h3>
        <p className="text-sm text-slate-600">{description}</p>
      </div>
    </div>
  );
}

export default function DistributorReps() {
  // Apply SEO meta tags for distributor rep job/opportunity targeting
  useSEO('distributorReps');
  
  const [status, setStatus] = useState({ state: 'idle', msg: '' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    distributor: '',
    state: '',
    territory: '',
    venmo_paypal: '',
    notes: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  async function onSubmit(e) {
    e.preventDefault();
    setStatus({ state: 'loading', msg: '' });

    const payload = {
      role: 'DistributorRep',
      ...formData
    };

    try {
      const res = await fetch(`${API}/distributor-rep-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data?.detail || 'Request failed');
      }
      
      setStatus({ state: 'success', msg: 'You\'re in! We\'ll send you access to the incentive board shortly.' });
      setFormData({
        name: '',
        email: '',
        phone: '',
        distributor: '',
        state: '',
        territory: '',
        venmo_paypal: '',
        notes: ''
      });
    } catch (err) {
      setStatus({ state: 'error', msg: err.message });
    }
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-navy" data-testid="distributor-hero">
        <div className="container-custom py-16 md:py-24">
          <span className="inline-block text-copper font-semibold text-sm tracking-wider uppercase mb-4">
            For Distributor Reps
          </span>
          
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Your incentives rarely reach you.
            <br />
            <span className="text-copper">This changes that.</span>
          </h1>
          
          <p className="text-white/80 text-lg md:text-xl max-w-3xl leading-relaxed mb-8">
            BevRelay's Incentive Board gives direct correlation between your work and your wallet. 
            Get paid directly via Venmo or PayPal when you land placements. No middlemen. No delays.
          </p>

          <div className="flex flex-wrap gap-4">
            <a href="#signup" className="btn-copper">
              Join Free — Start Earning
              <ArrowRight className="w-4 h-4 ml-2" />
            </a>
            <a href="#incentives" className="btn-outline">
              View Incentives
            </a>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-navy mb-4">
              The Problem We're Solving
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              Suppliers offer incentives. But those incentives get lost in the distributor machine — 
              filtered through layers of management, delayed by bureaucracy, or simply never communicated to the reps doing the work.
            </p>
            <p className="text-slate-700 text-lg font-medium">
              You're the one in the account. You're the one building relationships. You should see the reward.
            </p>
          </div>
        </div>
      </section>

      {/* Incentive Structure */}
      <section className="section-padding bg-white" id="incentives" data-testid="incentives-section">
        <div className="container-custom">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-navy mb-3">
            Current Incentive Board
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl">
            Earn direct payments for verified placements. Suppliers set the bounty — you collect when you deliver.
          </p>

          <div className="grid gap-6 md:grid-cols-2 mb-10">
            <IncentiveCard 
              icon={Wine}
              amount="200"
              title="New By-the-Glass or Cocktail Program"
              description="Land a new BTG wine or signature cocktail on a printed menu. New program = new revenue stream."
            />
            <IncentiveCard 
              icon={GlassWater}
              amount="100"
              title="Feature By-the-Glass or Cocktail"
              description="Get an existing product featured on printed menu materials. Features drive volume."
            />
          </div>

          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-6">
            <h3 className="font-semibold text-navy mb-3">Proof Requirements</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
              <div className="flex items-start gap-3">
                <Camera className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                <span>Photo of printed menu showing placement</span>
              </div>
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                <span>Copy of distributor order/invoice</span>
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              * Suppliers may adjust incentive amounts by request. All payouts verified before disbursement.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-navy mb-8">
            How It Works
          </h2>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <Step 
                number="1"
                title="Sign up for free"
                description="Join the BevRelay rep network. No cost, no obligation. Just opportunity."
              />
              <Step 
                number="2"
                title="Browse the incentive board"
                description="See which brands are offering bounties and what placements they're looking for."
              />
              <Step 
                number="3"
                title="Land the placement"
                description="Do what you do best — work your accounts and get products on menus."
              />
              <Step 
                number="4"
                title="Submit proof & get paid"
                description="Upload your proof (menu photo + order). Payment hits your Venmo/PayPal directly."
              />
            </div>

            <div className="rounded-2xl bg-navy p-8 text-white">
              <h3 className="font-heading text-xl font-semibold mb-4">Why Direct Payment?</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">No waiting for distributor payout cycles</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Clear correlation: your work = your money</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Transparent tracking of all submissions</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Paid via Venmo or PayPal — your choice</span>
                </li>
              </ul>

              <div className="mt-8 pt-6 border-t border-white/20">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-6 h-6 text-copper" />
                  <div>
                    <div className="font-semibold">Direct to your wallet</div>
                    <div className="text-sm text-white/70">Venmo • PayPal • Zelle (coming soon)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Signup Form */}
      <section className="section-padding bg-white" id="signup" data-testid="signup-section">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-navy mb-3">
                Join the Rep Network — Free
              </h2>
              <p className="text-slate-600">
                Sign up to access the incentive board and start earning on your placements.
              </p>
            </div>

            <form 
              onSubmit={onSubmit}
              className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
              data-testid="distributor-rep-form"
            >
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input 
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    placeholder="you@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Phone
                  </label>
                  <input 
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Distributor <span className="text-red-500">*</span>
                  </label>
                  <input 
                    name="distributor"
                    value={formData.distributor}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    placeholder="e.g., Southern Glazer's, RNDC..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input 
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    placeholder="e.g., IL, NV, HI..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Territory / Region
                  </label>
                  <input 
                    name="territory"
                    value={formData.territory}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="e.g., Chicago Metro, Las Vegas Strip..."
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-sm font-medium text-navy mb-2">
                  Venmo or PayPal Handle <span className="text-red-500">*</span>
                </label>
                <input 
                  name="venmo_paypal"
                  value={formData.venmo_paypal}
                  onChange={handleInputChange}
                  required
                  className="input-field"
                  placeholder="@yourhandle or email"
                />
                <p className="mt-1 text-xs text-slate-500">This is where we'll send your incentive payments</p>
              </div>

              <div className="mt-5">
                <label className="block text-sm font-medium text-navy mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="input-field resize-none"
                  placeholder="Tell us about your key accounts or anything else we should know..."
                />
              </div>

              <button
                type="submit"
                disabled={status.state === 'loading'}
                className="mt-6 w-full btn-copper justify-center disabled:opacity-60"
              >
                {status.state === 'loading' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Join the Network — It's Free
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>

              {status.state !== 'idle' && status.state !== 'loading' && (
                <div
                  className={`mt-4 rounded-xl px-4 py-3 flex items-start gap-3 ${
                    status.state === 'success' 
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                  role="alert"
                >
                  {status.state === 'success' ? (
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  )}
                  <span className="text-sm">{status.msg}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="rounded-3xl bg-navy p-10 md:p-16 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
              Your work. Your relationships. Your money.
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-8">
              Stop waiting for incentives that never come. Join BevRelay's rep network and get paid directly for the placements you land.
            </p>
            <a href="#signup" className="btn-copper inline-flex">
              Get Started — Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
